// SpotifyAlbumPicker — modal-style picker that calls /api/spotify/search-real
// and shows a list of real Spotify albums with cover, artist, name so the admin
// can click and pick the exact match.
import React, { useEffect, useState } from "react";
import { X, Loader2, Search, Music2, ExternalLink, Check } from "lucide-react";
import { spotifySearchReal } from "../services/discogs";

export default function SpotifyAlbumPicker({ open, onClose, onPick, initialQuery, initialBarcode }) {
  const [q, setQ] = useState(initialQuery || "");
  const [barcode, setBarcode] = useState(initialBarcode || "");
  const [albums, setAlbums] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [needsCreds, setNeedsCreds] = useState(false);

  useEffect(() => {
    if (open) {
      setQ(initialQuery || "");
      setBarcode(initialBarcode || "");
      setErr("");
      setNeedsCreds(false);
      setAlbums([]);
      // Auto-search on open
      if (initialQuery || initialBarcode) {
        setTimeout(() => doSearch(initialQuery || "", initialBarcode || ""), 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery, initialBarcode]);

  const doSearch = async (query, bc) => {
    setBusy(true);
    setErr("");
    setNeedsCreds(false);
    try {
      // Try to split "Artist Album" into fields; keep it simple
      const trimmed = (query || "").trim();
      const parts = trimmed.split(/\s+-\s+/);
      const artist = parts.length > 1 ? parts[0] : "";
      const album = parts.length > 1 ? parts.slice(1).join(" - ") : trimmed;
      const data = await spotifySearchReal({ artist, album, barcode: bc, limit: 12 });
      setAlbums(data.albums || []);
      if (!data.albums?.length) setErr("Heç bir nəticə tapılmadı. Axını dəyişdirin.");
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("Spotify credentials not configured") || msg.includes("400") || msg.includes("401")) {
        setNeedsCreds(true);
        setErr("Spotify credentials quraşdırılmayıb. Vault → API Açarları bölməsində əlavə edin.");
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    doSearch(q, barcode);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="spotify-picker">
      <div className="relative w-full max-w-2xl bg-[var(--surface)] border border-[var(--border)] shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Music2 size={16} className="text-[#1DB954]" />
            <div className="text-sm font-medium">Spotify Albom Seçən</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]">
            <X size={18} />
          </button>
        </div>

        {/* Search form */}
        <form onSubmit={submit} className="p-4 border-b border-[var(--border)] space-y-2">
          <div className="flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sənətçi — Albom (məs: Pink Floyd - Dark Side of the Moon)"
              className="input flex-1"
              autoFocus
              data-testid="spotify-picker-q"
            />
            <button type="submit" disabled={busy || (!q.trim() && !barcode.trim())} className="btn-primary px-4" style={{ textTransform: "none", letterSpacing: "0" }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <div className="text-[10px] tracking-[0.14em] text-[var(--text-muted)] font-medium">Vƌ YA UPC/BARKOD:</div>
            <input
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="məs: 8698540824082"
              className="input flex-1 font-mono text-sm"
              inputMode="numeric"
            />
          </div>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {err && (
            <div className="border border-[var(--accent)]/30 bg-[var(--accent)]/5 text-[var(--accent)] p-3 text-sm mb-3" data-testid="spotify-picker-err">
              {err}
              {needsCreds && (
                <div className="mt-2 text-xs text-[var(--text-muted)]">
                  Spotify Developer Dashboard-a keyd olun → App yaradın → Client ID + Secret-i Vault → API Açarları bölməsində sıxdırın.
                </div>
              )}
            </div>
          )}
          {!albums.length && !err && !busy && (
            <div className="text-center py-10 text-sm text-[var(--text-muted)]">
              Axını daxil edib əsil Spotify albom siyahısı alın. Barkod var sə daha dəqiqdir.
            </div>
          )}
          {busy && (
            <div className="text-center py-10">
              <Loader2 size={22} className="animate-spin mx-auto text-[#1DB954]" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {albums.map((a) => (
              <button
                key={a.id}
                onClick={() => { onPick && onPick(a); onClose && onClose(); }}
                className="group text-left border border-[var(--border)] hover:border-[#1DB954] p-3 bg-[var(--bg)] flex gap-3 transition-colors"
                data-testid={`spotify-picker-album-${a.id}`}
              >
                {a.cover ? (
                  <img src={a.cover} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 bg-black flex-shrink-0 flex items-center justify-center"><Music2 size={20} className="text-white/40" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-[var(--text-muted)] truncate">{a.artists}</div>
                  <div className="font-medium text-sm truncate">{a.name}</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-1">
                    {a.release_date} · {a.total_tracks} trek · {a.album_type}
                  </div>
                </div>
                <div className="self-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <Check size={16} className="text-[#1DB954]" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
